import { Box, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useCallback, useEffect, useRef } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import HtmlPreview from "@entrilia/shared/components/html/HtmlPreview";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { CitationReference } from "../../../../../api/types/workflowTypes";

interface Props {
  workflowId: string;
  activityId: string;
  documentName: string;
  citations: CitationReference[];
  selectedCitationId?: string;
}

const HtmlCitationsPreview = ({ workflowId, activityId, documentName, citations, selectedCitationId }: Props) => {
  const theme = useTheme();
  const htmlContainerRef = useRef<HTMLDivElement>(null);

  const allDeleted =
    citations.length > 0 && citations.every((c) => c.highlightResolution?.status === "DocumentDeleted");

  const getDocumentHtml = useCallback(
    (signal?: AbortSignal) =>
      adminApi.getWorkflowDocumentHtmlWithHighlights(workflowId, activityId, { documentName }, signal),
    [workflowId, activityId, documentName]
  );

  const [htmlDataResp, fetchError] = useFetch(getDocumentHtml);

  const markStyles = `mark[data-citation-id] {
      background-color: ${alpha(theme.palette.warning.light, 0.18)};
      color: inherit;
      border-radius: 2px;
    }`;

  const selectedMarkStyles = selectedCitationId
    ? `mark[data-citation-id="${selectedCitationId}"] {
         background-color: ${alpha(theme.palette.primary.main, 0.18)};
         outline: 2px solid ${theme.palette.primary.main};
       }`
    : "";

  useEffect(() => {
    if (!selectedCitationId || !htmlDataResp) {
      return;
    }

    const shadowRoot = htmlContainerRef.current?.firstElementChild?.shadowRoot;
    const mark = shadowRoot?.querySelector(`mark[data-citation-id="${selectedCitationId}"]`);
    mark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [selectedCitationId, htmlDataResp]);

  if (allDeleted) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
        <Typography color="textSecondary">Source removed — the original file was deleted by a user.</Typography>
      </Box>
    );
  }

  if (fetchError) {
    logError(fetchError, "[HtmlCitationsPreview] getWorkflowDocumentHtmlWithHighlights");
    return <DataLoadingFailed title="Could not load document content" />;
  }

  return (
    <Stack spacing={2.5} sx={{ flexGrow: 1, overflow: "auto", py: 2.5, px: 2.5 }}>
      <Typography variant="subtitle1">Source Content</Typography>
      {htmlDataResp ? (
        <HtmlPreview
          htmlContent={htmlDataResp.htmlContent}
          containerProps={{ ref: htmlContainerRef }}
          extraStyles={markStyles + selectedMarkStyles}
        />
      ) : (
        <InlineLoader />
      )}
    </Stack>
  );
};

export default HtmlCitationsPreview;
